import Corporate from '@/domain/Corporate'
import { MemberType, MemberTypes } from '@/domain/MemberType'
import { corporateRoles } from '@/domain/MyAccount'
import ProfessionalDetails from '@/domain/ProfessionalDetails'
import TravelDocument from '@/domain/TravelDocument'
import { joinArrayWithComma } from '@/infrastructure/Utils'

export default class CustomerProfile {
  static fromJSON(json) {
    return new CustomerProfile(json)
  }

  constructor(json) {
    this.assignValueOrNull('title', json.title)
    this.assignValueOrNull('firstName', json.firstName)
    this.assignValueOrNull('lastName', json.lastName)
    this.assignValueOrNull('countryCode', json.customerAddress?.countryCode)
    this.assignValueOrNull('email', json.email)
    this.assignValueOrNull('programType', json.programType)
    this.assignValueOrNull('profileType', json.profileType)
    this.assignValueOrNull('customerAddress', json.customerAddress)
    this.document = TravelDocument.fromJSON(json.document || {})
    this.assignValueOrNull('address', json.address)
    this.assignValueOrNull('postalCode', json.postalCode)
    this.assignValueOrNull('city', json.city)
    this.assignValueOrNull('state', json.state)
    this.assignValueOrNull('phone', json.phone)
    this.assignValueOrNull('tier', json.tier)
    this.assignValueOrNull('rewardsNumber', json.rewardsNumber)
    this.assignValueOrNull('language', json.language)
    this.assignValueOrNull('redeemPreferences', json.redeemPreferences)
    this.assignValueOrNull('sendPrintedMemberCard', json.sendPrintedMemberCard)
    this.assignValueOrNull('organizeMeetingsOrEvents', json.organizeMeetingsOrEvents)
    this.bookTripsForOthers = !!json.bookTripsForOthers
    this.assignValueOrNull('memberTypeExtended', json.memberTypeExtended)
    this.assignValueOrNull('travelAgent', json.travelAgent)
    this.assignValueOrNull('companyName', json.companyName)
    this.telephone = json.telephone || {}
    this.assignValueOrNull('isTravelAgent', json.isTravelAgent)
    this.assignValueOrNull('invitesLeft', json.invitesLeft)
    this.assignValueOrNull('birthDate', json.birthDate)
    this.assignValueOrNull('gender', json.gender)
    this.assignValueOrNull('placeOfBirth', json.placeOfBirth)
    this.assignValueOrNull('children', json.children)
    this.assignValueOrNull('crowd', json.crowd)
    this.assignValueOrNull('favorites', json.favorites)
    this.assignValueOrNull('reasonForTravel', json.reasonForTravel)
    this.professionalDetails = ProfessionalDetails.fromJSON(json.professionalDetails || {})
    this.assignValueOrNull('memberType', json.memberType)
    this.multifactor = json.multifactor || false
    this.corporateInfo = Corporate.fromJSON(json.corporateInfo || {})
  }

  assignValueOrNull = (key, value) => {
    this[key] = value || null
  }

  /* eslint-disable lines-between-class-members */
  isB2B = () => this.programType === 'B2B'
  isB2C = () => this.programType === 'B2C'
  isCorporate = () => this.programType === 'CORP'
  isOnlyTravelAgent = () => this.memberTypeExtended.name.includes(MemberType.TRAVEL_AGENT) // Add "Only" to the name to avoid conflicts with prop
  isTravelAgentME = () => this.memberTypeExtended.name.includes(MemberType.TRAVEL_AGENT_ME)
  isAnyTravelAgent = () => this.isOnlyTravelAgent() || this.isTravelAgentME()
  isTravelArranger = () => this.memberTypeExtended.name.includes(MemberType.TRAVEL_ARRANGER)
  isTravelArrangerME = () => this.memberTypeExtended.name.includes(MemberType.TRAVEL_ARRANGER_ME)
  isAnyTravelArranger = () => this.isTravelArranger() || this.isTravelArrangerME()
  isMeetingsAndEvents = () => this.memberTypeExtended.name.includes(MemberType.MEETINGS_AND_EVENTS)
  isAnyMeetingsAndEvents = () =>
    this.isMeetingsAndEvents() || this.isTravelArrangerME() || this.isTravelAgentME()
  isTMC = () => this.memberTypeExtended.name.includes('tmc')
  isPCR = () => this.memberTypeExtended.name.includes('pcr')
  isR4U = () => this.isTMC() || this.isPCR()
  isAdministrator = () => this.memberTypeExtended.name.includes(MemberType.ADMINISTRATOR)
  isDeputy = () => this.memberTypeExtended.name.includes(corporateRoles.DEPUTY)
  isBooker = () => this.memberTypeExtended.name.includes(corporateRoles.BOOKER)
  isAnyAdministrator = () =>
    ['B2B-A', 'B2B-DA', 'PCR-A', 'CORP-A', 'TMC-A'].some(adminType =>
      this.profileType.includes(adminType)
    )
  /* eslint-enable lines-between-class-members */

  getProfessionalRoleLabel() {
    return MemberTypes.find(role => role.code === this.memberTypeExtended.code)?.label
  }

  getReadableCatalogValues(catalogKey, listValues) {
    const catalog = listValues[catalogKey]
    const matches = catalog.reduce(
      (acc, curr) =>
        this.professionalDetails[catalogKey]?.includes(curr.code) ? [...acc, curr.name] : acc,
      []
    )
    return joinArrayWithComma(matches)
  }

  getCatalogTypeName(catalogDocTypes) {
    return catalogDocTypes.find(type => type.code === this.document.type).name
  }
}
